// src/components/dayClose/ContinuationItemsStep.tsx
// Step 4A: 未完成事項處理 - 今天有沒有想延續的事？

import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { DayCloseStepProps, ContinuationItem } from '../../types/dayClose';
import { TimeBox } from '../../types/timeBox';
import { ScheduleItem } from '../../types/schedule';
import { useDayCloseAPI } from '../../hooks/useDayCloseAPI';
import { getDayCloseById, updateDayClose } from '../../services/api/dayClose';
import { formatTime } from '../../utils/formatters';
import { timeBoxCategoryConfig } from '../../constants/timebox.constants';
import { logger } from '../../utils/logger';
import { useToast } from '../../contexts/ToastContext';
import { taskService } from '../../features/task';

type UnaddressedItem = ScheduleItem & {
  isSelected: boolean;
};

export const ContinuationItemsStep: React.FC<DayCloseStepProps> = ({
  dayCloseId,
  onComplete,
  userId,
  setLoading,
  setStepHandlers
}) => {
  const { showError, showSuccess } = useToast();
  const { loadTodayTimeBoxes, loadTodayEvents } = useDayCloseAPI();

  const [loading, setLoadingState] = useState(true);
  const [unaddressedItems, setUnaddressedItems] = useState<UnaddressedItem[]>([]);

  // 載入未處理的活動
  useEffect(() => {
    const loadUnaddressedItems = async () => {
      try {
        setLoadingState(true);

        // 獲取 meaningful activities
        const dayClose = await getDayCloseById(dayCloseId);
        const meaningfulActivityIds = new Set(
          (dayClose?.meaningfulActivities || [])
            .filter(a => a.sourceId)
            .map(a => a.sourceId!)
        );

        // 並行加載 TimeBoxes 和 Events
        const [timeBoxes, events] = await Promise.all([
          loadTodayTimeBoxes(userId),
          loadTodayEvents(userId)
        ]);

        // 過濾：排除 rest TimeBox 和 All day Events
        const filteredTimeBoxes = timeBoxes.filter(
          tb => tb.category !== 'rest' && tb.status !== 'cancelled'
        );
        const filteredEvents = events.filter(e => e.timeType === 'timed');

        // 找出沒有在 meaningful activities 中的項目
        const unaddressed: UnaddressedItem[] = [
          ...filteredTimeBoxes.filter(tb => !meaningfulActivityIds.has(tb.id)),
          ...filteredEvents.filter(e => !meaningfulActivityIds.has(e.id))
        ].map(item => ({
          ...item,
          isSelected: false
        }));

        // 按時間排序
        unaddressed.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());

        setUnaddressedItems(unaddressed);
      } catch (error) {
        logger.error('ContinuationItems', 'Failed to load unaddressed items', error);
        showError('Load Failed', 'Failed to load activities');
      } finally {
        setLoadingState(false);
      }
    };

    loadUnaddressedItems();
  }, [dayCloseId, userId, loadTodayTimeBoxes, loadTodayEvents, showError]);

  // 切換項目選擇
  const toggleItem = (index: number) => {
    setUnaddressedItems(prev => {
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], isSelected: !newItems[index].isSelected };
      return newItems;
    });
  };

  // 保存並繼續
  const handleContinue = async () => {
    try {
      setLoading?.(true);

      // 1. 收集選中的項目
      const selectedItems: ContinuationItem[] = unaddressedItems
        .filter(item => item.isSelected)
        .map(item => ({
          sourceId: item.id,
          sourceType: item.itemType === 'timebox' ? 'timeBox' : 'event',
          description: item.title
        }));

      // 2. 為每個選中項目創建 Task（進入 Inbox）
      const createdTaskIds: string[] = [];
      const failedItems: string[] = [];

      for (const item of selectedItems) {
        try {
          const task = await taskService.createTask({
            title: item.description,
            notes: `Continued from ${item.sourceType === 'timeBox' ? 'TimeBox' : 'Event'}: ${item.description}`,
            userId: userId,
            // 不設定 relatedTaskID 和 relatedTaskTitle（避免錯誤關聯）
            // 不設定 targetDate，讓 Task 進入 Inbox
            priority: '2',
            energyLevel: '2',
          });
          createdTaskIds.push(task.id);

          logger.info('ContinuationItems', 'Task created', {
            taskId: task.id,
            sourceId: item.sourceId
          });
        } catch (error) {
          failedItems.push(item.sourceId);
          logger.error('ContinuationItems', 'Failed to create task', {
            sourceId: item.sourceId,
            error
          });
          // 繼續處理其他項目，不中斷流程
        }
      }

      // 3. 保存到 DayClose
      await updateDayClose(dayCloseId, {
        continuationItems: selectedItems
      });

      logger.info('ContinuationItems', 'Items saved', {
        selectedCount: selectedItems.length,
        createdCount: createdTaskIds.length,
        failedCount: failedItems.length
      });

      // 4. 顯示用戶通知
      if (createdTaskIds.length > 0) {
        if (failedItems.length === 0) {
          // 全部成功
          showSuccess(
            'Tasks Created',
            `${createdTaskIds.length} task${createdTaskIds.length > 1 ? 's' : ''} added to your Inbox`
          );
        } else {
          // 部分成功
          showSuccess(
            'Tasks Created',
            `${createdTaskIds.length} task${createdTaskIds.length > 1 ? 's' : ''} added to Inbox, ${failedItems.length} failed`
          );
        }
      } else if (selectedItems.length > 0) {
        // 全部失敗
        showError('Creation Failed', 'Failed to create tasks. Please try again.');
        return; // 不進入下一步，讓用戶可以重試
      }
      // 如果沒有選中任何項目，直接進入下一步（不顯示通知）

      onComplete();
    } catch (error) {
      logger.error('ContinuationItems', 'Failed to save items', error);
      showError('Save Failed', 'Failed to save continuation items');
      // 不調用 onComplete()，讓用戶可以重試
    } finally {
      setLoading?.(false);
    }
  };

  // 註冊步驟處理函數到 Modal
  useEffect(() => {
    if (setStepHandlers) {
      setStepHandlers({
        handleContinuation: handleContinue
      });
    }

    return () => {
      if (setStepHandlers) {
        setStepHandlers({});
      }
    };
  }, [handleContinue, setStepHandlers]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  // 如果沒有未處理的項目，自動跳過
  if (unaddressedItems.length === 0) {
    setTimeout(() => onComplete(), 100);
    return (
      <div className="text-center py-12">
        <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p className="text-gray-500">All activities have been reviewed</p>
      </div>
    );
  }

  const selectedCount = unaddressedItems.filter(item => item.isSelected).length;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      {/* 標題 */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Anything you want to continue tomorrow?
        </h2>
        <p className="text-sm text-gray-500">
          These activities weren't marked as meaningful. Select any you'd like to continue.
        </p>
      </div>

      {/* 未處理項目列表 */}
      <div className="space-y-3 mb-6">
        {unaddressedItems.map((item, index) => {
          const isTimeBox = item.itemType === 'timebox';
          const config = isTimeBox
            ? timeBoxCategoryConfig[(item as TimeBox).category]
            : null;

          return (
            <button
              key={item.id}
              onClick={() => toggleItem(index)}
              className={`
                w-full p-4 rounded-lg border-2 transition-all text-left
                ${item.isSelected
                  ? 'border-sky-500 bg-sky-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
                }
              `}
            >
              <div className="flex items-start gap-3">
                {/* 勾選框 */}
                <div className={`
                  flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5
                  ${item.isSelected
                    ? 'bg-sky-500 border-sky-500'
                    : 'border-gray-300'
                  }
                `}>
                  {item.isSelected && (
                    <svg className="w-3 h-3 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                      <path d="M5 13l4 4L19 7"></path>
                    </svg>
                  )}
                </div>

                {/* 內容 */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm text-gray-500">
                      {formatTime(item.startTime)} - {formatTime(item.endTime)}
                    </span>
                    {config && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${config.color}`}>
                        {config.label}
                      </span>
                    )}
                  </div>
                  <p className="font-medium text-gray-900">
                    {item.title}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* 說明文字 */}
      {selectedCount > 0 && (
        <div className="p-3 bg-sky-50 border border-sky-100 rounded-lg">
          <p className="text-sm text-sky-800">
            <strong>{selectedCount}</strong> {selectedCount === 1 ? 'item' : 'items'} will be added to your Inbox
          </p>
        </div>
      )}
    </div>
  );
};
